/*
 *  Copyright (C) 2022 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Coordinate } from 'ol/coordinate';
import { GeometryObject, GeometryType, IMapBaseLayer } from 'sdi/source';
import { MiniStep } from 'sdi/map/mini';
import { Foldable, Nullable } from 'sdi/util';
import { HarvestedLayer } from '../components/query';
import { getFilter } from '../queries/mapnavigator';
import { Option } from 'fp-ts/lib/Option';

export type AppLayout =
    | 'map-fs'
    | 'map-and-info'
    | 'map-and-feature'
    | 'map-and-extract'
    | 'map-navigator-fs'
    | 'map-and-tracker'
    | 'map-and-measure'
    | 'print'
    | 'query'
    | 'query-print';

export enum SortDirection {
    ascending,
    descending,
}

export type TableDataKey = string;

export type IMenuData = Foldable;

export interface IToolWebServices extends Foldable {
    url: string;
    layers: IMapBaseLayer[];
}

export type LegendPage =
    | 'base-map'
    | 'data'
    | 'info'
    | 'locate'
    | 'locate-bookmark'
    | 'measure'
    | 'print'
    | 'share'
    | 'spatial-filter';

export interface ILegend {
    currentPage: LegendPage;
}

export interface TrackerCoordinate {
    coord: Coordinate;
    accuracy: number;
}

export interface IGeoTracker {
    track: TrackerCoordinate[];
    active: boolean;
}

export interface IPositioner {
    point: {
        latitude: number;
        longitude: number;
    };
}

export interface IGeoMeasure {
    active: boolean;
    geometryType: 'Polygon' | 'LineString';
    coordinates: Coordinate[];
}

export interface IShare {
    withView: boolean;
}

export const filterStatus = (status: string): HomeFilter => ({
    ...getFilter(),
    status,
});

export const filterCat = (categories: string[]): HomeFilter => ({
    ...getFilter(),
    categories,
});

export const filterRole = (roles: string[]): HomeFilter => ({
    ...getFilter(),
    roles,
});

export const filterPat = (pattern: string): HomeFilter => ({
    ...getFilter(),
    pattern,
});

export const defaultFilter = (): HomeFilter => ({
    roles: [],
    categories: [],
    pattern: null,
    status: null,
});

export type HomeFilter = {
    pattern: Nullable<string>;
    roles: string[];
    categories: string[];
    status: Nullable<string>;
};

export type HomeConfigTile = {
    mode: 'tile';
    filter: HomeFilter;
};

export type HomeTableKey =
    | 'colTitle'
    | 'colDescription'
    | 'colCategory'
    | 'colRole'
    | 'colDate'
    | 'colAuthor'
    | 'colStatus';

export type HomeTableSort = [HomeTableKey, 'asc' | 'desc'];

export type HomeConfigTable = {
    mode: 'table';
    filter: HomeFilter;
    sort: HomeTableSort;
};
export type HomeConfig = HomeConfigTile | HomeConfigTable;

export type HomeMode = HomeConfig['mode'];

export const defaultHomeConfigTile = (
    filter = defaultFilter(),
): HomeConfigTile => ({
    mode: 'tile',
    filter,
});

export const defaultHomeConfigTable = (
    filter = defaultFilter(),
): HomeConfigTable => ({
    mode: 'table',
    sort: ['colTitle', 'asc'],
    filter,
});

export type Idle = { state: 'idle' };
export const idle: Idle = { state: 'idle' as const };

export type CircleOptions = { state: 'circle-options'; radius: Option<number> };

export type HarvestShape = 'point' | 'line' | 'polygon' | 'circle';

export const geometryTypeOfShape = (shape: HarvestShape): GeometryType => {
    switch (shape) {
        case 'point':
        case 'circle':
            return 'Point';
        case 'line':
            return 'LineString';
        case 'polygon':
            return 'Polygon';
    }
};

export type NonCircleDrawing = {
    state: 'drawing';
    shape: Exclude<HarvestShape, 'circle'>;
};

export type CircleDrawing = {
    state: 'drawing';
    shape: 'circle';
    radius: number;
};

export type Drawing = NonCircleDrawing | CircleDrawing;

type HarvestingCommon = {
    state: 'harvesting';
    geometry: GeometryObject;
    results: HarvestedLayer[];
    minimap: MiniStep;
};

export type NonCircleHarvesting = HarvestingCommon & {
    shape: Exclude<HarvestShape, 'circle'>;
};

export type CircleHarvesting = HarvestingCommon & {
    shape: 'circle';
    center: Coordinate;
    radius: number;
};

export type Harvesting = NonCircleHarvesting | CircleHarvesting;

export type HarvestStep = Idle | CircleOptions | Drawing | Harvesting;
